import { useEffect, useRef, useState } from 'react';
import { getClaims, useAuth } from '@workos-inc/authkit-react';
import type { MeResponse } from '../api/me';
import { useFlagsSettled, useTermsGateEnabled, useTrialAutoStartEnabled } from '../lib/featureFlags';
import { isOrgAdmin } from '../lib/permissions';
import { FUNNEL, logEvent, useTrackFunnel } from '../lib/telemetry';
import { errorType } from '../lib/telemetry/errorType';
import {
  clearTrialIntent,
  consumeTermsAcceptedInSession,
  peekTrialIntent,
  readTrialIntent,
  trialSourceFields,
  type TrialIntent,
} from '../lib/trialIntent';
import { useStartTrial, type StartTrialOutcome } from './useStartTrial';

const RETRY_DELAY_MS = 2_000;
// The admin check's forced refresh is bounded like the post-start one (useStartTrial): a hung refresh
// degrades to the confirm button instead of holding the trial link, and the Try buttons, forever.
export const ADMIN_CHECK_TIMEOUT_MS = 5_000;

export type NoticeReason = 'non_admin' | 'unavailable' | 'already_trialled';
export type SkipReason =
  | NoticeReason
  | 'already_active'
  | 'terms_not_accepted'
  | 'terms_gate_off'
  | 'no_terms_marker'
  | 'failed';
export type TrialIntentPrompt =
  | { kind: 'confirm' }
  | { kind: 'retry' }
  | { kind: 'notice'; reason: NoticeReason }
  | null;
// 'auto': started unasked on this tab's terms acceptance; 'confirmed': the admin clicked the prompt.
type StartMode = 'auto' | 'confirmed';

type Options = {
  me: MeResponse | null;
  watch: (productKey: string) => void;
  refetch: () => void;
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function useTrialIntentAutoStart({ me, watch, refetch }: Options) {
  const auth = useAuth();
  const flagsSettled = useFlagsSettled();
  const enabled = useTrialAutoStartEnabled();
  const termsGate = useTermsGateEnabled();
  const trial = useStartTrial();
  const trackFunnel = useTrackFunnel();
  const handled = useRef(false);
  const starting = useRef(false);
  const termsReported = useRef(false);
  const adminCheck = useRef<Promise<boolean | null> | null>(null);
  // The intent a confirm or retry prompt was raised for: an explicit click acts on it even after the
  // TTL, which only guards starts nobody asked for.
  const promptIntent = useRef<TrialIntent | null>(null);
  const [prompt, setPrompt] = useState<TrialIntentPrompt>(null);
  const [busy, setBusy] = useState(false);

  // The intent's source plus the WorkOS organisation id: no email, no personal data.
  function sourceFields(intent: TrialIntent): Record<string, string> {
    const fields = trialSourceFields(intent);
    return auth.organizationId ? { ...fields, org_id: auth.organizationId } : fields;
  }

  // Every outcome goes to LaunchDarkly (the funnel) and App Insights alike, so a skip can be
  // diagnosed without LD access.
  function report(intent: TrialIntent, reason: SkipReason) {
    const fields = { ...sourceFields(intent), reason };
    trackFunnel(FUNNEL.TRIAL_AUTOSTART_SKIPPED, fields);
    logEvent('cloudhouse.enterprise.trial.autostart_skipped', 'Automatic trial start skipped', fields, 'INFO');
  }

  function reportStarted(intent: TrialIntent, mode: StartMode) {
    const fields = { ...sourceFields(intent), mode };
    trackFunnel(FUNNEL.TRIAL_AUTOSTARTED, fields);
    logEvent('cloudhouse.enterprise.trial.autostarted', 'Trial started from a website trial link', fields, 'INFO');
  }

  function ask(intent: TrialIntent, kind: 'confirm' | 'retry') {
    promptIntent.current = intent;
    setPrompt({ kind });
  }

  function settle() {
    clearTrialIntent();
    promptIntent.current = null;
  }

  function skip(intent: TrialIntent, reason: SkipReason, next: TrialIntentPrompt = null) {
    settle();
    report(intent, reason);
    setPrompt(next);
  }

  // One fresh-token admin check per mount, shared by the terms wait and the start; a failed check
  // is not kept, so a later attempt asks again.
  async function adminStatus(): Promise<boolean | null> {
    adminCheck.current ??= resolveAdmin();
    const admin = await adminCheck.current;
    if (admin === null) adminCheck.current = null;
    return admin;
  }

  async function resolveAdmin(): Promise<boolean | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>((resolve) => { timer = setTimeout(() => resolve('timeout'), ADMIN_CHECK_TIMEOUT_MS); });
    try {
      const token = await Promise.race([auth.getAccessToken({ forceRefresh: true }), timeout]);
      if (token === 'timeout') {
        logEvent('cloudhouse.enterprise.trial.token_refresh_timed_out', 'Token refresh before the trial admin check timed out',
          { timeout_ms: ADMIN_CHECK_TIMEOUT_MS }, 'WARN');
        return null;
      }
      return isOrgAdmin(getClaims(token).permissions);
    } catch (e) {
      logEvent('cloudhouse.enterprise.trial.token_refresh_failed', 'Token refresh before the trial admin check failed',
        { 'error.type': errorType(e) }, 'WARN');
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  async function startWithRetry(product: TrialIntent['product']): Promise<StartTrialOutcome> {
    const outcome = await trial.start(product);
    if (outcome !== 'transient') return outcome;
    await wait(RETRY_DELAY_MS);
    return trial.start(product);
  }

  function conclude(intent: TrialIntent, outcome: StartTrialOutcome, mode: StartMode) {
    if (outcome === 'started') {
      settle();
      reportStarted(intent, mode);
      watch(intent.product);
      refetch();
    } else if (outcome === 'forbidden') {
      skip(intent, 'non_admin', { kind: 'notice', reason: 'non_admin' });
    } else if (outcome === 'already') {
      skip(intent, 'already_trialled');
      refetch();
    } else if (outcome === 'transient') {
      report(intent, 'failed');
      logEvent('cloudhouse.enterprise.trial.autostart_failed', 'Automatic trial start failed after a retry',
        { ...sourceFields(intent), mode }, 'WARN');
      ask(intent, 'retry');
    } else {
      logEvent('cloudhouse.enterprise.trial.autostart_failed', 'Automatic trial start was refused',
        { ...sourceFields(intent), mode }, 'WARN');
      skip(intent, 'unavailable', { kind: 'notice', reason: 'unavailable' });
    }
  }

  async function begin(intent: TrialIntent, mode: StartMode) {
    if (starting.current) return;
    starting.current = true;
    setBusy(true);
    setPrompt(null);
    try {
      conclude(intent, await startWithRetry(intent.product), mode);
    } finally {
      starting.current = false;
      setBusy(false);
    }
  }

  // Settles a link whose trial /me says cannot start; true when the trial is on offer.
  function trialOffered(intent: TrialIntent, current: MeResponse): boolean {
    const product = current.products[intent.product];
    if (product?.trialAvailable === true) return true;
    if (!product) skip(intent, 'unavailable', { kind: 'notice', reason: 'unavailable' });
    else if (product.status === 'active') skip(intent, 'already_active');
    else skip(intent, 'already_trialled', { kind: 'notice', reason: 'already_trialled' });
    return false;
  }

  async function decide(intent: TrialIntent, current: MeResponse, termsPending: boolean) {
    // Only an acceptance this tab's /terms page just recorded lets the trial start unasked: an
    // existing admin who followed the link and signed in, or an org whose terms a colleague
    // accepted elsewhere, is asked. The proof is spent here, before any outcome, so no early
    // return leaves it behind for a later link to start on.
    const orgId = auth.organizationId ?? null;
    const termsProof = orgId !== null && consumeTermsAcceptedInSession(orgId);
    const admin = await adminStatus();
    if (admin === null) {
      report(intent, 'failed');
      ask(intent, 'confirm');
      return;
    }
    if (!admin) {
      skip(intent, 'non_admin', { kind: 'notice', reason: 'non_admin' });
      return;
    }
    if (!trialOffered(intent, current)) return;
    if (termsPending || !termsProof) {
      // With the terms gate off nobody routes this admin to /terms: a trial must still never start
      // unasked, so the intent degrades to the confirm button rather than silence.
      report(intent, termsPending ? 'terms_gate_off' : 'no_terms_marker');
      ask(intent, 'confirm');
      return;
    }
    await begin(intent, 'auto');
  }

  // Busy from the first await, so a product card's Try cannot race the automatic start.
  async function run(intent: TrialIntent, current: MeResponse, termsPending: boolean) {
    setBusy(true);
    try {
      await decide(intent, current, termsPending);
    } finally {
      if (!starting.current) setBusy(false);
    }
  }

  useEffect(() => {
    // Release flag off (or LaunchDarkly not settled, so the flag reads its default): nothing
    // happens — no confirm, no start, no notice, no metric. useTrialIntentRelease drops the intent.
    if (handled.current || auth.isLoading || !flagsSettled || !enabled || !me) return;
    const intent = readTrialIntent();
    if (!intent) return;
    const termsPending = me.terms?.accepted !== true;
    if (termsPending && termsGate) {
      // TermsGate sends an admin to /terms; wait for the acceptance to land in /me. Whether this is
      // an admin is settled first: a member is never sent there, so would otherwise wait forever.
      if (termsReported.current) return;
      termsReported.current = true;
      void (async () => {
        const admin = await adminStatus();
        if (handled.current) return; // the acceptance landed meanwhile and run() took over
        if (admin === false) {
          handled.current = true;
          skip(intent, 'non_admin', { kind: 'notice', reason: 'non_admin' });
          return;
        }
        report(intent, 'terms_not_accepted');
      })();
      return;
    }
    handled.current = true;
    void run(intent, me, termsPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when its inputs settle; handled/termsReported guard re-entry
  }, [auth.isLoading, flagsSettled, enabled, termsGate, me]);

  function start() {
    const intent = promptIntent.current ?? readTrialIntent();
    if (intent) void begin(intent, 'confirmed');
    else setPrompt(null);
  }

  // A trial started from a product card answers the link too: its confirm or retry prompt and the
  // stored intent go, and nothing later starts it again.
  function manualTrialStarted(productKey: string) {
    if ((promptIntent.current ?? peekTrialIntent())?.product !== productKey) return;
    handled.current = true;
    settle();
    setPrompt((current) => (current?.kind === 'notice' ? current : null));
  }

  return { prompt, busy, error: trial.error, start, dismiss: () => setPrompt(null), manualTrialStarted };
}
